import type { RadarData, Way } from "@radarspotter/types";
import dayjs from "dayjs";
import { MapPin, TriangleAlert } from "lucide-react";
import { Fragment } from "react";
import { formatDay, isRecent } from "@/lib/recent";

type Status = "loading" | "ready" | "error";

interface RadarListProps {
	data: RadarData | null;
	status: Status;
	onShowOnMap: (id: string) => void;
}

/** Panne de la source signalée au-delà d'une heure (un relevé raté n'alarme personne). */
const SOURCE_DOWN_AFTER_HOURS = 1;

function formatLastCheck(dateStr: string) {
	const date = dayjs(dateStr);
	const hoursDiff = dayjs().diff(date, "hour");

	if (hoursDiff < 100) {
		return date.fromNow();
	}

	const daysDiff = Math.floor(hoursDiff / 24);
	return `il y a ${daysDiff} jour${daysDiff > 1 ? "s" : ""}`;
}

const wayLabel = (way: Way) => (way ? `rocade ${way.toLowerCase()}` : "hors rocade");

const cardClass = "w-full my-[5px] rounded-[10px] p-[15px] bg-popover box-border";

function SourceDownBanner({ since, updatedAt }: { since: string; updatedAt: string | null }) {
	return (
		<div className={`${cardClass} flex items-start gap-2 border border-amber-400/30 text-sm text-amber-200`}>
			<TriangleAlert className="mt-0.5 size-4 shrink-0" />
			<span>
				La source ne répond plus depuis {dayjs(since).fromNow(true)} : les radars affichés datent du dernier relevé
				{updatedAt ? ` (${formatLastCheck(updatedAt)})` : ""}.
			</span>
		</div>
	);
}

export default function RadarList({ data, status, onShowOnMap }: RadarListProps) {
	const radars = data?.radars ?? [];
	const removed = data?.removed ?? [];
	const sortedRadars = [...radars].sort((a, b) =>
		a.way !== b.way ? b.way.localeCompare(a.way) : a.city.localeCompare(b.city),
	);
	const sourceDown =
		data?.sourceDownSince && dayjs().diff(dayjs(data.sourceDownSince), "hour") >= SOURCE_DOWN_AFTER_HOURS
			? data.sourceDownSince
			: null;

	return (
		<div className="flex flex-col items-center p-[5px] pb-[100px]">
			{status === "error" && (
				<span className={`${cardClass} text-center text-red-300`}>
					Impossible de charger les radars. Réessaie plus tard.
				</span>
			)}

			{status === "loading" &&
				[0, 1, 2, 3].map((i) => <div key={i} className={`${cardClass} h-[90px] animate-pulse opacity-40`} />)}

			{status === "ready" && sourceDown && <SourceDownBanner since={sourceDown} updatedAt={data?.updatedAt ?? null} />}

			{status === "ready" && data?.updatedAt && (
				<span className="text-sm text-center w-full my-[5px] rounded-[10px] p-[6px] bg-popover box-border">
					dernière màj: {formatLastCheck(data.updatedAt)}
				</span>
			)}

			{status === "ready" && sortedRadars.length === 0 && (
				<span className={`${cardClass} text-center`}>Aucun radar pour le moment.</span>
			)}

			{status === "ready" &&
				sortedRadars.map(({ id, city, road, installedAt, place, way, coords }, idx) => (
					<Fragment key={id}>
						{(idx === 0 || sortedRadars[idx - 1]?.way !== way) && (
							<span className="text-[25px] my-[15px] font-bold">{wayLabel(way)}</span>
						)}
						<div className={cardClass}>
							<div className="flex items-center justify-between gap-2">
								<div className="flex min-w-0 items-center gap-2">
									<span className="truncate text-[23px]">{city}</span>
									{isRecent(installedAt) && (
										<span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-xs font-semibold text-emerald-300">
											nouveau
										</span>
									)}
								</div>
								<div className="flex shrink-0 items-center gap-3">
									<button
										type="button"
										aria-label={`Voir ${city} sur la carte`}
										onClick={() => onShowOnMap(id)}
										className="cursor-pointer text-blue-400"
									>
										<MapPin className="size-[24px]" />
									</button>
									<button
										type="button"
										aria-label={`Ouvrir ${city} dans Google Maps`}
										onClick={() => window.open(`https://maps.google.com/maps?q=${coords[0]},${coords[1]}`)}
										className="cursor-pointer"
									>
										<img src="/gmaps.svg" alt="" className="w-[25px]" />
									</button>
								</div>
							</div>
							<i className="block text-xs">
								{road ? `${road} · ` : ""}installé le {formatDay(installedAt)}
							</i>
							<span className="block mt-[6px]">{place}</span>
						</div>
					</Fragment>
				))}

			{status === "ready" && removed.length > 0 && (
				<>
					<span className="text-[25px] my-[15px] font-bold">retirés récemment</span>
					{removed.map(({ id, city, road, way, removedAt }) => (
						<div key={`${id}-${removedAt}`} className={`${cardClass} opacity-60`}>
							<span className="block text-[20px] line-through decoration-1">{city}</span>
							<i className="block text-xs">
								{road ? `${road} · ` : ""}
								{wayLabel(way)} · retiré {dayjs(removedAt).fromNow()}
							</i>
						</div>
					))}
				</>
			)}
		</div>
	);
}
